import { useState, useEffect, Suspense, type FC } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { StateValidator } from "../components/StateValidator";
import { YearSwitchBanner } from "../components/YearSwitchBanner";
import { GlobalSessionHandler } from "../components/GlobalSessionHandler";
import { ScrollRestoration } from "../components/ScrollRestoration";
import { PrivateRoute } from "../components/PrivateRoute";
import { ReferrerDetector } from "../components/ReferrerDetector";
import { Footer } from "../components/Footer";
import { LoadingMask } from "../components/LoadingMask";
import { InjectIntlContext } from "../helpers/intlHooks";
import { useYearSwitchBanner } from "../helpers/yearHelpers";
import * as Pages from "../pages";
import messagesEn from "../translations/en-US.json";
import messagesEs from "../translations/es-MX.json";
import { routes } from "./routes";
import { RouteToastHandler } from "../components/RouteToastHandler";
import { IdleTimeout } from "../components/IdleTimeout/IdleTimeout";
import { MaxGlobalHeader } from "./MaxGlobalHeader";
import { useYearPart } from "../helpers/yearFlagHelpers";
import { AnalyticsListeners } from "../components/AnalyticsListeners";
import { IntlProvider } from "react-intl";
import { shouldPolyfill as shouldPolyfillRelativeTimeFormat } from "@formatjs/intl-relativetimeformat/should-polyfill";
import { shouldPolyfill as shouldPolyfillPluralRules } from "@formatjs/intl-pluralrules/should-polyfill";
import { AnonRoute } from "../components/AnonRoute";
import { getLanguageFromLocationOrState } from "../helpers/languageHelpers";
import { useAppContext } from "../helpers/context-hooks/useAppContext";
import { useBackendVersions } from "../helpers/query-hooks/useBackendVersions";
import { useMctFlags } from "../helpers/flag-hooks/useMctFlags";
import { useLoggedIn } from "../helpers/loginHelpers";
import { useSearchParams } from "../helpers/routing-hooks/useSearchParams";
import { isFromMedigap } from "../helpers/routeHelpers";
/**
 * For Safari & Mobile Safari, add a `scrollend` event listener polyfill
 * @TODO - remove this line and the dependency if Apple ever implements this spec
 */
import "scrollyfills";
import { MCT_APP_MAIN_ID } from "../config";
import { useLDClient } from "launchdarkly-react-client-sdk";
import { getLdUser } from "../helpers/launchDarklyHelpers";

import { ProviderNetworkRoute } from "../components/ProviderNetwork/ProviderNetworkRoute";
import type { TranslationKey } from "../@types";
import { DrawerManager } from "@cmsgov/ds-medicare-gov";
import { useEnableFacilitiesManagement } from "../helpers/context-hooks/useEnableFacilitiesManagement";

export const AppContent: FC = () => {
  // * Context
  const { state } = useAppContext();

  // * Queries
  // this prefetches the backend version numbers.
  const { hasAttempted } = useBackendVersions();

  // * Hooks
  const location = useLocation();
  const showYearSwitchBanner = useYearSwitchBanner();
  const isLoggedIn = useLoggedIn();
  const { selectedLanguage, selectedYear } = useSearchParams(true);
  const ldClient = useLDClient();

  // * Language
  const language = getLanguageFromLocationOrState({
    location,
    stateLang: state.language,
  });

  // For conditional render of the landing page route
  const url = window.location.href.split("/?")[0];
  const fromMedigap = isFromMedigap(url);

  // The IntlProvider adds language localization to its children with a default language and translations map as props
  const messages: Record<"en" | "es", Record<TranslationKey, string>> = {
    en: messagesEn,
    es: messagesEs as Record<TranslationKey, string>, // this will often be behind what English has, so we need to allow for missing keys
  };

  useEffect(() => {
    (async () => {
      await polyfill("en");
      await polyfill("es");
    })();
  }, []);

  // * Flags
  const flags = useMctFlags();
  const {
    feMaintenanceMode,
    tmpFeEnableProviderNetwork,
    feEnableVisualRegressionPages,
  } = flags;
  const enableFacilitiesManagement = useEnableFacilitiesManagement();
  const { isOutsideOpenEnrollment } = useYearPart();

  // * State
  const [ldFlagTimeout, setLdFlagTimeout] = useState<
    NodeJS.Timeout | undefined
  >();
  const [couldNotRetrieveLdFlags, setCouldNotRetrieveLdFlags] = useState(false);
  const [ldFlagsAvailable, setLdFlagsAvailable] = useState(false);

  // * Constants
  const isLoadingFlags = !(ldFlagsAvailable || couldNotRetrieveLdFlags);
  const isLoadingForTheFirstTime = isLoadingFlags || !hasAttempted;

  // * Effects
  useEffect(() => {
    const ldFlagsAreAvailable = Object.getOwnPropertyNames(flags).length > 0;

    setLdFlagsAvailable(ldFlagsAreAvailable);

    if (ldFlagsAreAvailable) {
      if (ldFlagTimeout) {
        clearTimeout(ldFlagTimeout);
        setLdFlagTimeout(undefined);
      }
      setCouldNotRetrieveLdFlags(false);
    } else {
      setLdFlagTimeout(
        setTimeout(() => {
          setCouldNotRetrieveLdFlags(true);
        }, 5000)
      );
    }
    // ! TODO: See MCT-11398
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This error has been grandfathered in and needs to be fixed.
  }, [flags]);

  useEffect(() => {
    ldClient?.identify(getLdUser(state.beneficiary));
  }, [state.beneficiary, ldClient]);

  return (
    <IntlProvider locale={language} messages={messages[language]}>
      <InjectIntlContext>
        <MaxGlobalHeader>
          <StateValidator>
            <ScrollRestoration>
              <ReferrerDetector>
                <GlobalSessionHandler>
                  <IdleTimeout>
                    <AnalyticsListeners>
                      <RouteToastHandler>
                        <div className="ds-base mct-l-app">
                          {showYearSwitchBanner && <YearSwitchBanner />}
                          <Suspense
                            fallback={
                              <LoadingMask
                                loading={true}
                                message={
                                  messages[state.language]["app.loading"]
                                }
                              />
                            }
                          >
                            <DrawerManager>
                              {isLoadingForTheFirstTime ? (
                                <Pages.LoadingPage />
                              ) : (
                                <div className="AppContent mct-l-app__content">
                                  <main
                                    id={`${MCT_APP_MAIN_ID}`}
                                    data-testid={`${MCT_APP_MAIN_ID}`}
                                    className="app__main mct-l-app__main e2e-app-main"
                                    tabIndex={-1}
                                  >
                                    {feMaintenanceMode ||
                                    couldNotRetrieveLdFlags ? (
                                      <Pages.MaintenancePage />
                                    ) : (
                                      <Routes>
                                        {feEnableVisualRegressionPages && (
                                          <Route
                                            path={
                                              routes.visualRegression.components
                                            }
                                            element={
                                              <Pages.VisualRegressionComponentsPage />
                                            }
                                          />
                                        )}
                                        <Route
                                          path={routes.searchResults}
                                          element={<Pages.SearchResultsPage />}
                                        />
                                        <Route
                                          path={routes.sanctionedPlans}
                                          element={
                                            <Pages.SanctionedPlansPage />
                                          }
                                        />
                                        <Route
                                          path={routes.drugSearchPreferences}
                                          element={
                                            <Pages.DrugSearchPreferencesPage />
                                          }
                                        />
                                        {!isOutsideOpenEnrollment && (
                                          <Route
                                            path={routes.yearOverYear}
                                            element={<Pages.PlanComparePage />}
                                          />
                                        )}
                                        <Route
                                          path={routes.comparePlans}
                                          element={<Pages.PlanComparePage />}
                                        />
                                        <Route
                                          path={routes.planDetails}
                                          element={<Pages.PlanDetailsPage />}
                                        />
                                        <Route
                                          path={routes.managePrescriptions}
                                          element={
                                            <Pages.ManagePrescriptionsPage />
                                          }
                                        />
                                        <Route
                                          path={routes.prescriptionsList}
                                          element={
                                            <Pages.ManagePrescriptionsPage />
                                          }
                                        />
                                        <Route
                                          path={routes.managePharmacies}
                                          element={
                                            <Pages.ManagePharmaciesPage />
                                          }
                                        />
                                        <Route
                                          path={routes.inNetworkPharmacy}
                                          element={
                                            <Pages.InNetworkPharmaciesPage />
                                          }
                                        />
                                        <Route
                                          path={routes.pharmacy}
                                          element={
                                            <Pages.SelectPharmaciesPage />
                                          }
                                        />
                                        <Route
                                          path={routes.questionRouting}
                                          element={
                                            <Pages.QuestionRoutingPage />
                                          }
                                        />
                                        <Route
                                          path={routes.lisQuestions}
                                          element={<Pages.LisQuestionsPage />}
                                        />
                                        <Route
                                          path={routes.enroll}
                                          element={<Pages.EnrollmentFormPage />}
                                        />
                                        <Route
                                          path={routes.csrLanding}
                                          element={<Pages.CSRLandingPage />}
                                        />
                                        <Route
                                          path={routes.pdePage}
                                          element={<Pages.PDEPage />}
                                        />
                                        <Route
                                          path={routes.version}
                                          element={<Pages.VersionPage />}
                                        />
                                        <Route
                                          path={routes.planPreview}
                                          element={<Pages.PlanPreviewPage />}
                                        />
                                        <Route
                                          path={routes.coverageWizard.options}
                                          element={
                                            <Pages.CoverageWizardOptionsPage />
                                          }
                                        />
                                        <Route
                                          path={routes.coverageWizard.info}
                                          element={
                                            <Pages.CoverageWizardInfoPage />
                                          }
                                        />
                                        <Route
                                          path={
                                            routes.coverageWizard.landingPage
                                          }
                                          element={
                                            <Pages.CoverageWizardLandingPage />
                                          }
                                        />
                                        <Route
                                          path={routes.pace.plans}
                                          element={<Pages.PacePlansPage />}
                                        />
                                        <Route
                                          path={routes.pace.landingPage}
                                          element={<Pages.PaceLandingPage />}
                                        />
                                        <Route
                                          path={routes.pap.plans}
                                          element={<Pages.PapPlansPage />}
                                        />
                                        <Route
                                          path={routes.spap.plans}
                                          element={<Pages.SpapPlansPage />}
                                        />
                                        <Route
                                          path={routes.spap.landingPage}
                                          element={<Pages.SpapLandingPage />}
                                        />
                                        <Route
                                          path={routes.summary.lisQuestions}
                                          element={
                                            <PrivateRoute>
                                              <Pages.LisQuestionsPage
                                                isFromSummaryPage={true}
                                              />
                                            </PrivateRoute>
                                          }
                                        />
                                        <Route
                                          path={routes.summary.managePharmacies}
                                          element={
                                            <PrivateRoute>
                                              <Pages.ManagePharmaciesPage
                                                isFromSummaryPage={true}
                                              />
                                            </PrivateRoute>
                                          }
                                        />
                                        <Route
                                          path={
                                            routes.summary.managePrescriptions
                                          }
                                          element={
                                            <PrivateRoute>
                                              <Pages.ManagePrescriptionsPage
                                                isFromSummaryPage={true}
                                              />
                                            </PrivateRoute>
                                          }
                                        />
                                        <Route
                                          path={routes.summary.selectPharmacies}
                                          element={
                                            <PrivateRoute>
                                              <Pages.SelectPharmaciesPage
                                                isFromSummaryPage={true}
                                              />
                                            </PrivateRoute>
                                          }
                                        />
                                        <Route
                                          path={
                                            routes.summary.drugSearchPreferences
                                          }
                                          element={
                                            <PrivateRoute>
                                              <Pages.DrugSearchPreferencesPage
                                                isFromSummaryPage={true}
                                              />
                                            </PrivateRoute>
                                          }
                                        />
                                        <Route
                                          path={routes.summary.pdePage}
                                          element={
                                            <PrivateRoute>
                                              <Pages.PDEPage
                                                isFromSummaryPage={true}
                                              />
                                            </PrivateRoute>
                                          }
                                        />
                                        <Route
                                          path={routes.summary.searchResults}
                                          element={
                                            <PrivateRoute>
                                              <Pages.SearchResultsPage />
                                            </PrivateRoute>
                                          }
                                        />
                                        {tmpFeEnableProviderNetwork && (
                                          <Route
                                            path={
                                              routes.summary.manageProviders
                                            }
                                            element={
                                              <PrivateRoute>
                                                <Pages.ManageProvidersPage
                                                  isFromSummaryPage={true}
                                                />
                                              </PrivateRoute>
                                            }
                                          />
                                        )}
                                        {enableFacilitiesManagement && (
                                          <>
                                            <Route
                                              path={
                                                routes.providerNetwork
                                                  .addFacilities
                                              }
                                              element={
                                                <Pages.AddFacilitiesPage />
                                              }
                                            />
                                            <Route
                                              path={
                                                routes.providerNetwork
                                                  .manageFacilities
                                              }
                                              element={
                                                <Pages.ManageFacilitiesPage />
                                              }
                                            />
                                          </>
                                        )}
                                        <Route
                                          path={routes.summary.landingPage}
                                          element={
                                            <PrivateRoute>
                                              <Pages.SummaryPage />
                                            </PrivateRoute>
                                          }
                                        />
                                        <Route
                                          path={routes.pap.landingPage}
                                          element={<Pages.PapLandingPage />}
                                        />
                                        <Route
                                          path={routes.logout}
                                          element={<Pages.LogoutPage />}
                                        />
                                        <Route
                                          path={routes.medigap.plans}
                                          element={<Pages.MedigapPlans />}
                                        />
                                        <Route
                                          path={routes.medigap.planDetails}
                                          element={
                                            <Pages.MedigapPlanDetailsPage />
                                          }
                                        />
                                        <Route
                                          path={routes.medigap.policies}
                                          element={
                                            <Pages.MedigapPoliciesPage />
                                          }
                                        />
                                        <Route
                                          path={routes.medigap.landingPage}
                                          element={<Pages.MedigapLandingPage />}
                                        />
                                        <Route
                                          path={routes.newToMedicare}
                                          element={
                                            <AnonRoute>
                                              <Pages.NewToMedicarePage />
                                            </AnonRoute>
                                          }
                                        />
                                        <Route
                                          path={routes.findPlansNow}
                                          element={<Pages.FindPlansNowPage />}
                                        />
                                        <Route
                                          path={routes.csrGuestAccess}
                                          element={<Pages.CSRGuestAccessPage />}
                                        />
                                        <Route
                                          path={
                                            routes.providerNetwork.addProviders
                                          }
                                          element={
                                            <ProviderNetworkRoute>
                                              <Pages.AddProvidersPage />
                                            </ProviderNetworkRoute>
                                          }
                                        />
                                        <Route
                                          path={
                                            routes.providerNetwork
                                              .manageProviders
                                          }
                                          element={
                                            <ProviderNetworkRoute>
                                              <Pages.ManageProvidersPage />
                                            </ProviderNetworkRoute>
                                          }
                                        />
                                        <Route
                                          path="*"
                                          element={
                                            // RE: Medigap exception
                                            // @see https://github.com/CMSgov/oc-mct-frontend/pull/2293
                                            isLoggedIn && !fromMedigap ? (
                                              <Navigate
                                                to={`${routes.summary.landingPage}?year=${selectedYear}&lang=${selectedLanguage}`}
                                                state={{
                                                  from: location,
                                                }}
                                                replace
                                              />
                                            ) : (
                                              <Pages.LandingPage />
                                            )
                                          }
                                        />
                                      </Routes>
                                    )}
                                  </main>
                                  {!location.hash.includes(
                                    routes.planPreview.replace("/:id", "")
                                  ) && <Footer />}
                                </div>
                              )}
                            </DrawerManager>
                          </Suspense>
                        </div>
                      </RouteToastHandler>
                    </AnalyticsListeners>
                  </IdleTimeout>
                </GlobalSessionHandler>
              </ReferrerDetector>
            </ScrollRestoration>
          </StateValidator>
        </MaxGlobalHeader>
      </InjectIntlContext>
    </IntlProvider>
  );
};

async function polyfill(locale: string) {
  const unsupportedRelativeTimeLocale =
    shouldPolyfillRelativeTimeFormat(locale);
  const unsupportedPluralRulesLocale = shouldPolyfillPluralRules(locale);

  if (unsupportedRelativeTimeLocale) {
    // Load the polyfill 1st BEFORE loading data
    await import("@formatjs/intl-relativetimeformat/polyfill-force");
    await import(
      `@formatjs/intl-relativetimeformat/locale-data/${unsupportedRelativeTimeLocale}`
    );
  }

  if (unsupportedPluralRulesLocale) {
    // Load the polyfill 1st BEFORE loading data
    await import("@formatjs/intl-pluralrules/polyfill-force");
    await import(
      `@formatjs/intl-pluralrules/locale-data/${unsupportedPluralRulesLocale}`
    );
  }
}
